<script setup>
import { ref, shallowRef, onBeforeMount, onBeforeUnmount, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useOrgStore } from '@/stores/org'
import { useUserStore } from '@/stores/user'
import { useAppStore } from '@/stores/app'
import { API, HOST } from '@/utils/http'
import * as z from 'zod'
import { useAuth } from '@/composables/useAuth'

import { Input } from '@/components/ui/input'
import { Form, FormField, FormItem, FormControl, FormLabel, FormMessage } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { SkylabPorthole } from '@/layouts'
import JoinLinkHero from './JoinLinkHero.vue'
import DialogQrCode from '@/components/dialogs/DialogQrCode.vue'

const router = useRouter()
const route = useRoute()
const orgStore = useOrgStore()
const userStore = useUserStore()
const appStore = useAppStore()
const { initAuthListener } = useAuth()

const token = route.params.token
const linkBase = `${HOST}/join-link/${token}`
const isLoggedIn = computed(() => appStore.isLoggedIn)

const link = ref(null)
const linkError = ref(null)
const isLoading = ref(true)
const isJoining = ref(false)
const isShowForm = ref(false)
const outcome = ref(null) // 'pending' once a request was sent
const formMode = ref('signup') // 'signup' for a newcomer, 'join' for an account missing a name
const firebaseUser = ref(null)
const initialValues = ref(null)
let unsubscribeAuth = null

const qrOpen = ref(false)
const pageUrl = `${location.origin}/join/${token}`

const copyLink = async () => {
  try {
    await navigator.clipboard.writeText(pageUrl)
    toast({ title: 'Join link copied' })
  } catch (e) {
    toast({ title: "Couldn't copy the link", variant: 'destructive' })
  }
}

const INACTIVE_COPY = {
  expired: 'This join link has expired.',
  revoked: 'This join link is no longer active.',
  exhausted: 'This join link has reached its limit of members.',
}

const state = computed(() => {
  if (isLoading.value) return 'loading'
  if (isShowForm.value) return 'form'
  if (linkError.value === 404) return 'not_found'
  if (linkError.value || !link.value) return 'error'
  if (outcome.value === 'pending') return 'pending'
  if (link.value.state !== 'active') return 'inactive'
  return firebaseUser.value ? 'ready' : 'ready_signed_out'
})

const formSchema = shallowRef(z.object({
  first_name: z.string().trim().min(1, 'First name is required').max(255),
  last_name: z.string().trim().min(1, 'Last name is required').max(255),
}))

const signInRoute = () => ({ name: 'SignIn', query: { redirect: `${route.path}?accept=1` } })

const failToast = (description) => toast({ title: 'Unable to join', description, variant: 'destructive' })

const apiErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.detail || fallback

const getLink = async () => {
  try {
    link.value = (await API({ method: 'GET', url: linkBase })).data || null
    linkError.value = null
  } catch (error) {
    link.value = null
    linkError.value = error.response?.status || 'network'
  }
}

const retry = async () => {
  isLoading.value = true
  await getLink()
  isLoading.value = false
}

/** Make sure the backend session (JWT + user store member) exists for the current Firebase user. */
const ensureSession = async () => {
  if (isLoggedIn.value) return { success: true }
  return userStore.startSession(firebaseUser.value)
}

const openProject = async () => {
  const orgId = link.value.organization_id
  await orgStore.fetchOrgs()
  orgStore.setSelectedOrg(String(orgId))
  router.push({ name: 'Project', params: { org_id: orgId, project_id: link.value.project_id } })
}

/** Join with the session that exists; the backend answers joined, pending or member. `names` fill an empty name. */
const joinWithSession = async (names = undefined) => {
  const { status } = (await API({ method: 'POST', url: `${linkBase}/join`, data: names })).data
  if (status === 'pending') {
    outcome.value = 'pending'
    return
  }
  await openProject()
}

const splitName = (displayName) => {
  const parts = (displayName || '').trim().split(/\s+/).filter(Boolean)
  return { first: parts[0] || '', last: parts.slice(1).join(' ') }
}

const isNamed = (member) => !!member?.first_name?.trim() && !!member?.last_name?.trim()

/** The name form, which can't be skipped: prefilled from the account, then from the Google profile. */
const askForName = (mode) => {
  const member = userStore.member
  const derived = splitName(member?.display_name || firebaseUser.value?.displayName)
  formMode.value = mode
  initialValues.value = {
    first_name: member?.first_name?.trim() || derived.first,
    last_name: member?.last_name?.trim() || derived.last,
  }
  isShowForm.value = true
}

const isNameRequired = (error) => error?.response?.status === 422 && error.response.data?.detail?.error === 'NAME_REQUIRED'

const joinProject = async () => {
  if (!firebaseUser.value) {
    router.push(signInRoute())
    return
  }
  if (isJoining.value) return

  isJoining.value = true
  try {
    const session = await ensureSession()
    if (session.success) {
      if (!isNamed(userStore.member)) {
        askForName('join')
        return
      }
      await joinWithSession()
      return
    }
    if (session.reason !== 'user_not_registered') {
      failToast('Could not sign you in. Please try again.')
      return
    }
    askForName('signup')
  } catch (error) {
    if (isNameRequired(error)) {
      askForName('join')
      return
    }
    if (error.response?.status === 410) {
      await getLink()
      return
    }
    failToast(apiErrorMessage(error, 'Failed to join. Please try again.'))
  } finally {
    isJoining.value = false
  }
}

async function onSubmit(values) {
  const names = { first_name: values.first_name.trim(), last_name: values.last_name.trim() }
  if (formMode.value === 'join') {
    try {
      await joinWithSession(names)
      isShowForm.value = false
      userStore.setMember({ ...userStore.member, ...names })
    } catch (error) {
      failToast(apiErrorMessage(error, 'Failed to join. Please try again.'))
    }
    return
  }
  try {
    const firebaseToken = await firebaseUser.value.getIdToken(true)
    const result = await userStore.handleAuthentication({
      firebaseToken,
      joinToken: token,
      inviteDetails: names,
    })
    if (!result.success) {
      failToast(result.reason === 'error'
        ? apiErrorMessage(result.error, 'Failed to join. Please try again.')
        : `Authentication failed: ${result.reason}`)
      return
    }
    isShowForm.value = false
    await joinWithSession()
  } catch (error) {
    failToast(apiErrorMessage(error, 'Failed to join. Please try again.'))
  }
}

const close = async () => {
  if (!firebaseUser.value) {
    router.push({ name: 'SignIn' })
    return
  }
  const session = await ensureSession()
  router.push(session.success ? { name: 'Dashboard' } : { name: 'SignIn' })
}

const switchAccount = async () => {
  await appStore.logout()
  router.push(signInRoute())
}

onBeforeMount(() => {
  unsubscribeAuth = initAuthListener(async (user) => {
    firebaseUser.value = user
    await getLink()
    isLoading.value = false

    if (user && route.query.accept === '1' && state.value === 'ready') {
      router.replace({ path: route.path })
      joinProject()
    }
  })
})

onBeforeUnmount(() => {
  if (unsubscribeAuth) unsubscribeAuth()
})
</script>

<template>
  <SkylabPorthole>
    <div class="w-full max-w-sm bg-card text-foreground rounded-2xl border border-border shadow-[0_40px_100px_-15px_rgba(0,0,0,0.9),0_0_120px_-20px_rgba(255,255,255,0.15)] px-6 py-6 overflow-hidden" data-testid="join-page">
      <JoinLinkHero v-if="link && !['loading', 'not_found', 'error'].includes(state)" :link="link" class="-mx-6 -mt-6 mb-5" />
      <div v-if="state === 'loading'" class="md:p-1 text-center">
        <p class="mt-4 mb-3 text-gray-500">Loading...</p>
      </div>

      <div v-else-if="state === 'not_found'" class="md:p-1 text-center">
        <p class="mt-4 mb-3">This join link is invalid.</p>
        <Button @click="close">Close</Button>
      </div>

      <div v-else-if="state === 'inactive'" data-testid="join-inactive">
        <p class="mb-1">{{ INACTIVE_COPY[link.state] || 'This join link is no longer active.' }}</p>
        <p class="mb-4 text-sm text-gray-500">Ask the project team for a new one.</p>
        <Button @click="close">Close</Button>
      </div>

      <div v-else-if="state === 'pending'" data-testid="join-pending">
        <p class="mb-1">Your request to join was sent.</p>
        <p class="mb-4 text-sm text-gray-500">You'll get access once the project team approves it.</p>
        <Button @click="close">Close</Button>
      </div>

      <div v-else-if="state === 'ready_signed_out'" class="text-left">
        <div class="flex flex-wrap gap-2">
          <Button size="lg" class="font-semibold" @click="joinProject">Sign in to join</Button>
          <Button size="lg" variant="outline" @click="close">Close</Button>
        </div>
        <div class="mt-3"><button type="button" class="text-sm text-gray-500 underline cursor-pointer" data-testid="join-qr" @click="qrOpen = true">Show QR</button></div>
      </div>

      <div v-else-if="state === 'ready'" class="text-left">
        <p class="text-sm text-gray-500">Joining as {{ firebaseUser?.email || firebaseUser?.phoneNumber }}</p>
        <div class="mt-4 flex flex-wrap gap-2">
          <Button size="lg" class="font-semibold" :disabled="isJoining" data-testid="join-button" @click="joinProject">
            {{ isJoining ? 'Joining...' : link.requires_approval ? 'Request to join' : 'Join' }}
          </Button>
          <Button size="lg" variant="outline" @click="close">Close</Button>
        </div>
        <div class="mt-3 flex flex-wrap gap-x-4 gap-y-1">
          <button type="button" class="text-sm text-gray-500 underline cursor-pointer" @click="switchAccount">Not you? Switch account</button>
          <button type="button" class="text-sm text-gray-500 underline cursor-pointer" data-testid="join-qr" @click="qrOpen = true">Show QR</button>
        </div>
      </div>

      <div v-else-if="state === 'form'">
        <div class="mb-3">
          <h2 class="font-bold">What's your name?</h2>
          <p class="text-gray-500 text-sm">The project team sees it when you join.</p>
        </div>

        <Form v-if="initialValues" data-testid="join-name-form" :form-schema="formSchema" :initial-values="initialValues" class="space-y-4" @submit="onSubmit">
          <FormField v-slot="{ componentField }" name="first_name">
            <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input v-bind="componentField" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>

          <FormField v-slot="{ componentField }" name="last_name">
            <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl>
                <Input v-bind="componentField" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>

          <div class="flex justify-end">
            <Button type="submit" data-testid="join-name-submit">{{ link.requires_approval ? 'Request to join' : 'Join' }}</Button>
          </div>
        </Form>
      </div>

      <div v-else class="md:p-1 text-center">
        <p class="mt-4 mb-3">We couldn't load this join link. Check your connection and try again.</p>
        <div class="space-x-2">
          <Button @click="retry">Retry</Button>
          <Button variant="outline" @click="close">Close</Button>
        </div>
      </div>
    </div>
    <DialogQrCode v-if="link" v-model:open="qrOpen" :url="pageUrl" :title="link.project_name" @copy="copyLink" />
  </SkylabPorthole>
</template>
